import { useState } from 'react';
import { X, BookOpen } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { updateNode } from '@/services/api';
import type { FlowNode } from '@/types/flow';
import TemplatePickerDialog from '@/pages/Meta/TemplateLibrary/components/TemplatePickerDialog';
import {
    applyTemplateToNodeConfig,
    nodeTypeSupportsTemplate,
} from '@/pages/Meta/TemplateLibrary/lib/applyTemplateToNodeConfig';
import type { TemplateLibraryItem } from '@/types/template-library';
import TriggerConfig from './config/TriggerConfig';
import MessageConfig from './config/MessageConfig';
import DelayConfig from './config/DelayConfig';
import MessageTemplateConfig from "@/pages/Meta/Flow/Builder/components/config/MessageTemplateConfig.tsx";
import PhotoTemplateConfig from "@/pages/Meta/Flow/Builder/components/config/PhotoTemplateConfig.tsx";
import PhotoConfig from "@/pages/Meta/Flow/Builder/components/config/PhotoConfig.tsx";
import VideoTemplateConfig from "@/pages/Meta/Flow/Builder/components/config/VideoTemplateConfig.tsx";
import VideoConfig from "@/pages/Meta/Flow/Builder/components/config/VideoConfig.tsx";
import AudioConfig from "@/pages/Meta/Flow/Builder/components/config/AudioConfig.tsx";
import TagConfig from "@/pages/Meta/Flow/Builder/components/config/TagConfig.tsx";
import GoToConfig from "@/pages/Meta/Flow/Builder/components/config/GoToConfig.tsx";
import ReplyConfig from "@/pages/Meta/Flow/Builder/components/config/ReplyConfig.tsx";
import CommentTriggerConfig from "@/pages/Meta/Flow/Builder/components/config/CommentTriggerConfig.tsx";
import WaitClickConfig from "@/pages/Meta/Flow/Builder/components/config/WaitClickConfig.tsx";
import BlocksEditor from './BlocksEditor';
import type { CommentAutoReply } from '@/services/api/comment-auto-replies';

// Node types that support composite blocks
const BLOCK_SUPPORTED_TYPES = ['message', 'photo', 'video', 'audio', 'message_template', 'photo_template', 'video_template'];

interface NodePanelProps {
    node: FlowNode;
    onClose: () => void;
    page_id: string;
    builderMode?: 'flow' | 'comment_auto_reply';
    ruleData?: CommentAutoReply;
    funnelPageIds?: string[];
}

export default function NodePanel({ node, onClose, page_id, builderMode, ruleData, funnelPageIds }: NodePanelProps) {
    const nodeType = node.data.node_type;
    const [pickerOpen, setPickerOpen] = useState(false);
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const showTemplatePicker = nodeTypeSupportsTemplate(nodeType);

    const applyTemplateMutation = useMutation({
        mutationFn: (template: TemplateLibraryItem) => {
            const newConfig = applyTemplateToNodeConfig(
                template, nodeType, node.data.config,
            );
            return updateNode(node.data.id, { config: newConfig });
        },
        onSuccess: () => {
            toast({
                title: 'Modelo aplicado',
                description: 'Reabra o node pra ajustar os campos antes de salvar.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose(); // força reload do painel com os novos values
        },
        onError: (e: any) => {
            toast({
                title: 'Falha ao aplicar modelo',
                description: e?.response?.data?.detail || e?.message || 'erro desconhecido',
                variant: 'destructive',
            });
        },
    });

    const getNodeTypeInfo = () => {
        switch (nodeType) {
            case 'trigger':
                return {
                    title: 'Configuração do Gatilho',
                    subtitle: 'Configure quando o fluxo deve iniciar',
                    color: 'bg-cyan-500',
                    icon: '⚡',
                };
            case 'message':
                return {
                    title: 'Configuração da Mensagem',
                    subtitle: 'Personalize o título e conteúdo',
                    color: 'bg-blue-500',
                    icon: '💬',
                };
            case 'delay':
                return {
                    title: 'Configuração do Delay',
                    subtitle: 'Defina o tempo de espera',
                    color: 'bg-orange-500',
                    icon: '⏱️',
                };
            case 'message_template':
                return {
                    title: 'Configuração Template de mensagem',
                    subtitle: 'Defina o template',
                    color: 'bg-blue-300',
                    icon: '⏱️',
                };
            case 'wait_click':
                return {
                    title: 'Esperar Click',
                    subtitle: 'Configure o tempo e ramos do wait click',
                    color: 'bg-orange-500',
                    icon: '🖱️',
                };
            default:
                return {
                    title: 'Configurar Node',
                    subtitle: '',
                    color: 'bg-gray-500',
                    icon: '⚙️',
                };
        }
    };

    const info = getNodeTypeInfo();
    const supportsBlocks = BLOCK_SUPPORTED_TYPES.includes(nodeType);

    return (
        <div className="absolute top-0 bottom-0 right-0 w-[480px] bg-white shadow-2xl z-40 flex flex-col">
            <TemplatePickerDialog
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                onSelect={(t) => applyTemplateMutation.mutate(t)}
            />

            {/* Header */}
            <div className={`${info.color} px-6 py-6 text-white flex-shrink-0`}>
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div>
                            <h2 className="text-xl font-semibold">{info.title}</h2>
                            <p className="text-sm text-white/90 mt-1">{info.subtitle}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {showTemplatePicker && (
                            <button
                                onClick={() => setPickerOpen(true)}
                                disabled={applyTemplateMutation.isPending}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-colors disabled:opacity-50"
                                title="Aplicar modelo da biblioteca"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                Usar modelo
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Content - Config + Blocks in single scroll */}
            <div className="flex-1 overflow-y-auto">
                {nodeType === 'trigger' && builderMode === 'comment_auto_reply' && ruleData && (
                    <CommentTriggerConfig node={node} onClose={onClose} ruleData={ruleData} />
                )}
                {nodeType === 'trigger' && builderMode !== 'comment_auto_reply' && <TriggerConfig node={node} onClose={onClose} />}
                {nodeType === 'message' && <MessageConfig node={node} onClose={onClose} />}
                {nodeType === 'delay' && <DelayConfig node={node} onClose={onClose} />}
                {nodeType === 'message_template' && <MessageTemplateConfig node={node} onClose={onClose} pageId={page_id} />}
                {nodeType === 'photo_template' && <PhotoTemplateConfig node={node} onClose={onClose} pageId={page_id} />}
                {nodeType === 'photo' && (<PhotoConfig node={node} onClose={onClose} pageId={page_id} />)}
                {nodeType === 'video_template' && (<VideoTemplateConfig node={node} onClose={onClose} pageId={page_id} />)}
                {nodeType === 'video' && (<VideoConfig node={node} onClose={onClose} pageId={page_id} />)}
                {nodeType === 'audio' && (<AudioConfig node={node} onClose={onClose} pageId={page_id} />)}
                {nodeType === 'tag' && (<TagConfig node={node} onClose={onClose} />)}
                {nodeType === 'go_to' && (<GoToConfig node={node} onClose={onClose} currentFunnelPageIds={funnelPageIds} />)}
                {nodeType === 'reply' && (<ReplyConfig node={node} onClose={onClose} />)}
                {nodeType === 'wait_click' && (<WaitClickConfig node={node} onClose={onClose} />)}

                {/* Blocks Editor - below config for supported types */}
                {supportsBlocks && (
                    <BlocksEditor
                        nodeId={node.data.id}
                        initialBlocks={node.data.config?.blocks || []}
                        pageId={page_id}
                    />
                )}
            </div>
        </div>
    );
}
